import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Table } from 'tea-component';

import {
  dealwithSortItem,
  tableColumnsMap,
} from '../../../constants';
import CardCommon from '@op/components/CardCommon';
import useKeywordColumn from '@op/hooks/useKeywordColumn';
import BubbleCommon, {
  communityPostKeywordsTipsText,
  communityVideoKeywordsTipsText,
} from '@op/components/BubbleCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import { getLocale } from 'umi';
import { getTeaLocale } from '@/pages/home/utils/lang';
import moment from 'moment';
import UserGuideCommunity from '@op/components/UserGuide/community';
import { useDrilldownParams } from '@op/hooks/useDrilldownParams';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import useMetricsHook from '@op/components/FilterBoxCommon/hooks/useMetricsHook';
import { NO_DATE } from '@op/constants';

import { useTableColumns, useTableData } from '@op/pages/Influencer/hooks';
import useTableSelect, { TableColorType } from '@op/hooks/useTableSelect';
import { useRequestFontWebConfig } from '@op/hooks/useFrontWebConfig';
import { useFilterCondConf } from '@op/hooks/useFilterCondConf';
import { EIGHTBALL_EDITION_ID } from '@op/pages/community/video/components/utils';
import { arrayToObj } from '@/pages/opinion/utils';
import { JumpType } from '@home/utils/utils';
import useLanguage from '@op/hooks/useLanguage';
import '@op/pages/Influencer/scss/index.scss';
import './index.scss';
const { pageable, autotip, sortable, scrollable, stylize } = Table.addons;

export default function InfluencerTable({
  params,
  mediaType,
  children,
  wholeGameInfo,
  gameInfo,
  hasKolTagsFilter = false,
  filterOptionsKey,
  defaultPageSize,
  tableDefaultSortKey,
  hasIndex = true,
  jumpType,
}: {
  params: any;
  mediaType: string;
  children: ({
    selectedKeysMap,
    loading,
  }: {
    selectedKeysMap: TableColorType;
    loading: boolean;
  }) => JSX.Element;
  wholeGameInfo: any;
  gameInfo: any;
  hasKolTagsFilter?: boolean;
  filterOptionsKey?: any;
  defaultPageSize?: number;
  tableDefaultSortKey?: string;
  hasIndex?: boolean;
  jumpType?: JumpType;
}) {
  // 这个key很重要，决定了多选的key值，决定了表格返回的recodeKey， rowKey等，所以单独定义
  const tableKey = 'user_id';
  const { formatMessage, isEn: isEN } = useLanguage();
  const { downloadLegacy, downloading } = useDownloadParams();
  const { goPage } = useDrilldownParams({ jumpType });
  const isEightBall = gameInfo?.edition_unified_id === EIGHTBALL_EDITION_ID;
  const { kol_tags_brand_options = [], loading: fontWebLoading } = useRequestFontWebConfig({ keys: [{ key: 'kol_tags_brand_options' }], gameInfo, needLoading: true });
  const { author_black_gray_threat_types: blackGrayThreatTypes = [] } = useFilterCondConf({
    gameInfo,
    needRequest: isEightBall,
    requestList: [{ key: 'author_black_gray_threat_types', defaultValue: [] }],
  });
  const tagList = useMemo(() => {
    const kolTags = kol_tags_brand_options?.map((val) => {
      const textEN = val?.text || val?.textCN;
      const textCN = val?.textCN || val?.text;
      const text = isEN ? textEN : textCN;
      return {
        ...val,
        text,
        downloadText: textEN,
        value: val?.value,
      };
    }) || [];
    const blackGrayTags = (blackGrayThreatTypes || []).map((item: any) => {
      const textEN = item?.label_en || item?.label_zh;
      const textCN = item?.label_zh || item?.label_en;
      const text = isEN ? textEN : textCN;
      return {
        ...item,
        text,
        textCN,
        downloadText: textEN,
        value: item?.value,
      };
    });
    return [...kolTags, ...blackGrayTags];
  }, [kol_tags_brand_options, isEN, blackGrayThreatTypes]);

  const [tags, setTags] = useState<Array<string>>([]);
  useEffect(() => {
    // 标签内容没变时不更新 state，避免 newParams 对象重建触发重复请求
    const nextTags =
      tagList?.filter((val) => !val?.no_filter).map((item) => item.value) || [];
    setTags((prevTags) => {
      const isSame =
        prevTags.length === nextTags.length &&
        prevTags.every((item, index) => item === nextTags[index]);
      return isSame ? prevTags : nextTags;
    });
  }, [tagList]);

  const [progressVisible, setProgressVisible] = useState(true);
  // 表格全选框状态， 选中的相关数据
  const {
    saveDefaultData,
    selectKeys,
    allSelectInfo,
    selectedKeysMap,
    commonIndexColumns,
  } = useTableSelect({
    tableKey,
  });

  /* 处理keyword并发，导致没有数据的问题 */
  const { setRecord, KeywordColumn, keywordMap } = useKeywordColumn({
    commonParams: params,
    dealwithParams: (item, { channels, ...otherParams }) => {
      const onlyId = item.user_id;

      return {
        ...otherParams,
        keyword_top_n: 5,
        keyword_sample_size: '40',
        anchor_md5s: [onlyId],
        channel: channels,
        social_media_by_account: ['99999'],
        social_media_by_keyword: ['99999'],
        external_data_sources: ['99999'],
      };
    },
    dealwithKey: (param: any) => param?.anchor_md5s?.[0],
  });

  const newParams = useMemo(
    () => {
      if (fontWebLoading) {
        return {};
      }
      return {
        ...params,
        kol_tags_types: tags.length === tagList.length ? [] : tags,
      };
    },
    [fontWebLoading, params, tagList.length, tags],
  );

  /* table数据的所有相关内容 */
  const {
    keyword,
    setKeyword,
    sort,
    getTableRecord,
    changeSortInfo,
    linkTime,
    total,
    apiError,
    pageInfo,
    changePageInfo,
    loading,
    chartMaxValue,
  } = useTableData({
    params: newParams,
    mediaType,
    saveDefaultData,
    allSelectInfo,
    setLoadingInfo: setRecord,
    defaultPageSize,
    tableDefaultSortKey,
  });

  const metricsOptions = useMemo(() => {
    const options = tableColumnsMap[mediaType].map(
      (item: { header: any; key: any }) => {
        return {
          text: formatMessage({ id: item.header }),
          value: item.key,
        };
      },
    );
    options.unshift({
      text: formatMessage({ id: 'Tags' }),
      value: 'tags',
    });
    if (mediaType !== 'stream') {
      options.push({
        text: formatMessage({ id: 'Keywords' }),
        value: 'keywords',
      });
    }
    return filterOptionsKey
      ? options?.filter((item: { value: any }) =>
        filterOptionsKey?.includes(item?.value),
      )
      : options;
  }, [mediaType, formatMessage]);

  const { filterChange, getTotalColumns, showColumns } = useMetricsHook({
    filterColumns: ['influencer'],
    metricsOptions,
  });

  /* 表格列的相关内容 */
  const { tableColumns, sortKeys } = useTableColumns({
    linkTime,
    mediaType,
    tableKey,
    selectKeys,
    selectedKeysMap,
    sort,
    changeSortInfo,
    progressVisible,
    params,
    isAuto: showColumns.length === 0,
    tags,
    setTags,
    tagList,
    chartMaxValue,
    gameInfo: {
      ...gameInfo,
      cover: wholeGameInfo?.cover,
    },
    jumpType,
  });

  // keyword列处理
  const keywordColumn = useMemo(() => {
    if (mediaType === 'stream') {
      return [];
    }
    const tipsMap: any = {
      video: communityVideoKeywordsTipsText,
      post: communityPostKeywordsTipsText,
    };

    return [
      {
        key: 'keywords',
        header: (
          <BubbleCommon
            placement="top"
            tipsMap={tipsMap[mediaType]}
            showIcon={false}
          >
            <div className="dbo-influencer-table-header">
              {formatMessage({ id: 'Keywords' })}
            </div>
          </BubbleCommon>
        ),
        align: 'left',
        width: 500,
        render: (item: any) => {
          const onlyId = item.user_id;
          return (
            <KeywordColumn
              dataKey={onlyId}
              itemClick={(children) => {
                return goPage({
                  path: `/opinion/Stats/Detail${gameInfo?.edition_unified_id ? `?gameid=${gameInfo?.edition_unified_id}` : ''}`,
                  filter: {
                    filter: {
                      ...params,
                      ...gameInfo,
                      keyword: children,
                      anchor_md5s: [onlyId],
                      keyword_widely_match: true,
                      social_media_by_account: ['99999'],
                      social_media_by_keyword: ['99999'],
                      external_data_sources: ['99999'],
                    },
                    statsDetailType: 'influencer-keyword',
                  },
                });
              }}
            ></KeywordColumn>
          );
        },
      },
    ];
  }, [mediaType, keywordMap, formatMessage]);

  const locale = getTeaLocale(getLocale());
  const records = getTableRecord();

  const totalTableColumns = useMemo(() => {
    return [...tableColumns, ...keywordColumn];
  }, [tableColumns, keywordColumn]);

  return (
    <div>
      {children({ selectedKeysMap, loading })}
      <FilterBoxCommon
        downloadLoading={downloading}
        left={[
          {
            key: 'metrics',
            defaultValue: showColumns,
            defaultParams: {
              options: metricsOptions,
            },
          },
        ]}
        right={[
          {
            key: 'search',
            defaultParams: {
              className: 'dbo-influencer-search',
              placeholder: formatMessage({
                id: 'Search Influencer name.',
              }),
              onSearch: (value: any) => {
                setKeyword(value);
              },
              onClear: () => {
                setKeyword('');
              },
            },
          },
          // {
          //   key: 'show_progress',
          //   defaultValue: progressVisible,
          //   defaultParams: {
          //     bubbleParams: {
          //       content: formatMessage({
          //         id: !progressVisible
          //           ? 'Hide comparison chart of column data. Comparison chart calculation method: Based on the maximum value of the column where the value resides,  use a bar chart to show the proportion of the value to the maximum value.'
          //           : 'Show comparison chart of column data. Comparison chart calculation method: Based on the maximum value of the column where the value resides,  use a bar chart to show the proportion of the value to the maximum value.',
          //       }),
          //     },
          //     style: { marginRight: 10 },
          //   },
          // },
          {
            key: 'download',
            event: {
              onClick: () => {
                const downloadLimit = 150000; // 下载限制条数
                const gameName = wholeGameInfo?.entity_name || '';
                const fileName = `Opinions_${gameName}_Influencer_${moment(
                  params.start_time,
                ).format('YYYYMMDD')}_${moment(params.end_time).format(
                  'YYYYMMDD',
                )}`;
                let sortItem = sort.by;
                if (
                  ['followers_grow_more_5k', 'followers_grow'].includes(sort.by)
                ) {
                  sortItem = 'followers';
                }
                downloadLegacy({
                  limit: downloadLimit,
                  fileName,
                  params: dealwithSortItem(
                    {
                      ...newParams,
                      anchor_name: keyword,
                      page: 1,
                      page_size: 1000,
                      sort_item: sortItem,
                      sort_model: sort.order,
                      extraParams: {
                        tagList: arrayToObj(tagList, 'value'),
                      }
                    },
                    mediaType === 'stream',
                  ),
                  downloadFunc: 'getInfluencerCreatorList',
                  sheetName: 'Influencer',
                  total,
                });
              },
            },
          },
        ]}
        onChange={(value, key) => {
          const valueInfo = value[key!];
          if (key === 'show_progress') {
            setProgressVisible(valueInfo);
          }
          filterChange(value, key);
        }}
      ></FilterBoxCommon>
      <CardCommon className="dbo-influencer-chartbox overall-conext">
        <div className="influencer-table-wrapper">
          <ConfigProvider locale={locale}>
            {/* dbo-table-sticky表头吸顶，默认在顶部，如果需要在筛选器吸顶下方，需要加--withfilter */}
            <UserGuideCommunity></UserGuideCommunity>
            <CardCommon
              loadingHeight={350}
              loading={loading}
              // emptyHeight={350}
              // empty={records?.length === 0}
              contentCard
            >
              <div style={{ position: 'relative' }}>
                <div className="user-guide-influencer-mask"></div>
                <Table
                  className="dbo-table-sticky dbo-table-sticky--withfilter dbo-influencer-table dbo-influencer-table--selectable"
                  bordered="all"
                  records={records}
                  recordKey={tableKey}
                  columns={
                    hasIndex
                      ? [
                        commonIndexColumns({
                          headerInfo: (
                            <span className="dbo-influencer-table-header">
                              #
                            </span>
                          ),
                          dealwithItemElement: ({ defaultIndex }) =>
                            defaultIndex,
                          pageInfo,
                        }),
                        ...getTotalColumns(totalTableColumns),
                      ]
                      : getTotalColumns(totalTableColumns)
                  }
                  addons={[
                    pageable({
                      recordCount: total,
                      pageIndex: pageInfo.page,
                      pageSize: pageInfo.page_size,
                      onPagingChange: ({ pageSize, pageIndex }) => {
                        changePageInfo(pageIndex, pageSize);
                      },
                    }),
                    autotip({
                      isLoading: loading,
                      emptyText: formatMessage({ id: NO_DATE.SELECTION }),
                    }),
                    sortable({
                      columns: sortKeys.map((key) => ({
                        key: key,
                        prefer: 'desc',
                      })),
                      value: [sort],
                      onChange: (_value, { sort }) => {
                        changeSortInfo(sort);
                      },
                    }),
                    scrollable({
                      minWidth: 'auto',
                    }),
                    stylize({
                      // headClassName: 'dbo-influencer-table-head tea-table__header',
                    }),
                  ]}
                />
              </div>
            </CardCommon>
          </ConfigProvider>
        </div>
      </CardCommon>
    </div>
  );
}
